/**
 * Production Console Suppression
 * 
 * This module provides a production-safe way to handle console logging.
 * In development: All console methods work normally
 * In production: Debug logs are suppressed, errors remain visible
 */

// Check if we're in production mode
const isProduction = import.meta.env.PROD;

/**
 * Production-safe console wrapper
 * Automatically suppresses debug logs in production while preserving errors
 */
class ProductionConsole {
  private originalConsole: typeof console;
  
  constructor() {
    this.originalConsole = { ...console };
    this.initializeConsole();
  }

  private initializeConsole() {
    if (isProduction) {
      // In production: suppress debug logs, keep errors
      console.log = () => {};
      console.warn = () => {};
      console.info = () => {};
      console.debug = () => {};
      console.table = () => {};
      console.group = () => {};
      console.groupEnd = () => {};
      console.time = () => {};
      console.timeEnd = () => {};
      
      // Keep console.error for production error tracking
      console.error = this.originalConsole.error;
    }
    // In development: everything works normally (no changes needed)
  }

  /**
   * Restore original console (useful for testing)
   */
  restore() {
    Object.assign(console, this.originalConsole);
  }

  /**
   * Get current environment info
   */
  getEnvironmentInfo() {
    return {
      isProduction,
      isDevelopment: !isProduction,
      suppressedMethods: isProduction ? ['log', 'warn', 'info', 'debug', 'table', 'group', 'groupEnd', 'time', 'timeEnd'] : [],
      preservedMethods: ['error']
    };
  }
}

// Initialize production console suppression
const productionConsole = new ProductionConsole();

// Export utilities for manual console usage if needed
export const devConsole = {
  /**
   * Development-only logging - suppressed in production
   */
  log: (...args: unknown[]) => {
    if (!isProduction) {
      productionConsole['originalConsole'].log(...args);
    }
  },

  warn: (...args: unknown[]) => {
    if (!isProduction) {
      productionConsole['originalConsole'].warn(...args);
    }
  },

  info: (...args: unknown[]) => {
    if (!isProduction) {
      productionConsole['originalConsole'].info(...args);
    }
  },

  debug: (...args: unknown[]) => {
    if (!isProduction) {
      productionConsole['originalConsole'].debug(...args);
    }
  },

  /**
   * Always shown (both dev and production) for error tracking
   */
  error: (...args: unknown[]) => {
    productionConsole['originalConsole'].error(...args);
  },

  table: (data: unknown, columns?: string[]) => {
    if (!isProduction) {
      productionConsole['originalConsole'].table(data, columns);
    }
  }
};

/**
 * Conditional logging utility
 * @param condition - When true, log will be shown even in production
 * @param message - Message to log
 */
export const conditionalLog = (condition: boolean, ...args: unknown[]) => {
  if (!isProduction || condition) {
    productionConsole['originalConsole'].log(...args);
  }
};

/**
 * Performance logging - only in development
 */
export const logPerformance = <T>(label: string, fn: () => T): T => {
  if (!isProduction) {
    const start = performance.now();
    const result = fn();
    const end = performance.now();
    productionConsole['originalConsole'].log(`⏱️  ${label}: ${(end - start).toFixed(2)}ms`);
    return result;
  }
  return fn();
};

// Environment info
export const isProduction_ = isProduction;
export const isDevelopment = !isProduction;

// Export the console instance for advanced usage
export { productionConsole };